import React, { forwardRef, useRef, useImperativeHandle } from "react";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import { FormControlLabel } from "@mui/material";
const DISABLED_COLOR = "var(--text-light)";
const ENABLED_BORDER_COLOR = "var(--surface-stroke-gradient-color1)";
const GRADIENT_BACKGROUND =
  "linear-gradient(100.44deg, var(--gradient-color) 0%, var(--primary-gradient-color2) 110.4%)";
const ICON_SIZE = 16;
const BORDER_RADIUS = "0.125rem";
const BORDER_WIDTH = "1px";
const INNER_ICON_FONT_SIZE = 12;
interface CustomIconProps {
  disabled?: boolean;
}
/**
 * Syncfusion-shaped change args: the real React change event with the extra fields that
 * `handleChange` layers on with `Object.create`.
 */
type CheckBoxChangeArgs = React.ChangeEvent<HTMLInputElement> & {
  event: React.ChangeEvent<HTMLInputElement>;
  checked: boolean;
  value: string;
  name?: string;
};

interface CheckBoxComponentProps extends Omit<CheckboxProps, "onChange"> {
  label?: string;
  enabled?: boolean;
  change?: (args: CheckBoxChangeArgs, checked: boolean) => void;
  onChange?: (args: CheckBoxChangeArgs, checked: boolean) => void;
}
const getCommonStyles = ({ disabled }: CustomIconProps) => {
  return {
    width: ICON_SIZE,
    height: ICON_SIZE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    borderRadius: BORDER_RADIUS,
    boxSizing: "border-box",
    "& .MuiSvgIcon-root": {
      fontSize: INNER_ICON_FONT_SIZE,
    },
    opacity: disabled ? "25%" : "100%",
  };
};
const CustomUncheckedIcon: React.FC<CustomIconProps> = ({ disabled }) => (
  <Box
    sx={{
      ...getCommonStyles({ disabled }),
      border: disabled ? "none" : `${BORDER_WIDTH} solid ${DISABLED_COLOR}`,
      backgroundColor: disabled ? DISABLED_COLOR : "transparent",
    }}
  />
);
const CustomCheckedIcon: React.FC<CustomIconProps> = ({ disabled }) => (
  <Box
    sx={{
      ...getCommonStyles({ disabled }),
      border: disabled ? "none" : `${BORDER_WIDTH} solid ${ENABLED_BORDER_COLOR}`,
      background: disabled ? DISABLED_COLOR : GRADIENT_BACKGROUND,
    }}
  >
    <CheckIcon />
  </Box>
);
const CustomIndeterminateIcon: React.FC<CustomIconProps> = ({ disabled }) => (
  <Box
    sx={{
      ...getCommonStyles({ disabled }),
      border: disabled ? "none" : `${BORDER_WIDTH} solid ${ENABLED_BORDER_COLOR}`,
      background: disabled ? DISABLED_COLOR : GRADIENT_BACKGROUND,
    }}
  >
    <HorizontalRuleIcon />
  </Box>
);
interface CheckBoxRef {
  focus: () => void;
  blur: () => void;
  element: HTMLButtonElement | null;
  current: HTMLButtonElement | null;
  checked: boolean | undefined;
}

const CheckBoxComponent = forwardRef<CheckBoxRef, CheckBoxComponentProps>((props, ref) => {
  const { disabled: disabledProp, enabled, indeterminate, sx, label, onChange, change, ...restProps } = props;

  const internalRef = useRef<HTMLButtonElement>(null);

  const disabled = enabled !== undefined ? !enabled : disabledProp;

  useImperativeHandle(ref, () => ({
    focus: () => internalRef.current?.focus(),
    blur: () => internalRef.current?.blur(),

    get element() {
      return internalRef.current;
    },
    get current() {
      return internalRef.current;
    },

    get checked() {
      return props.checked;
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const syncfusionArgs: CheckBoxChangeArgs = Object.create(e, {
      event: { value: e, enumerable: true },
      checked: { value: checked, enumerable: true },
      value: { value: e.target.value, enumerable: true },
      name: { value: e.target.name, enumerable: true },
      target: { value: e.target, enumerable: true },
      currentTarget: { value: e.currentTarget, enumerable: true },
    });

    if (onChange) {
      onChange(syncfusionArgs, checked);
    }

    if (change) {
      change(syncfusionArgs, checked);
    }
  };

  const checkboxElement = (
    <Checkbox
      ref={internalRef}
      icon={<CustomUncheckedIcon disabled={disabled} />}
      checkedIcon={<CustomCheckedIcon disabled={disabled} />}
      indeterminateIcon={<CustomIndeterminateIcon disabled={disabled} />}
      indeterminate={indeterminate}
      disabled={disabled}
      onChange={handleChange}
      sx={{
        ...sx,
      }}
      {...restProps}
    />
  );

  if (label) {
    // cehck if laebl idf present
    return (
      <FormControlLabel
        control={checkboxElement}
        label={label}
        disabled={disabled}
        sx={{
          marginInlineStart: 0,
          marginInlineEnd: 0,
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem",
            color: disabled ? "var(--text-light)" : "inherit",
            alignItems: "center",
          },
        }}
      />
    );
  }

  return checkboxElement;
});
CheckBoxComponent.displayName = "CheckBoxComponent";
export default CheckBoxComponent;
